import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Smartphone, MessageCircle, MapPin, Images, FileText, Search, Zap, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FEATURES = [
  { icon: Smartphone, t: "Mobile-Friendly Design", s: "Har device par perfect dikhe" },
  { icon: MessageCircle, t: "WhatsApp Integration", s: "Customers seedhe WhatsApp par" },
  { icon: MapPin, t: "Google Maps", s: "Location easily dhoondein" },
  { icon: FileText, t: "Services / Menu", s: "Apni services showcase karein" },
  { icon: Images, t: "Photo Gallery", s: "High-quality business photos" },
  { icon: Search, t: "SEO-Friendly", s: "Google par aasani se milein" },
];

export default function GetWebsite() {
  useSeo("Get Your Business Website | OyeBest", "Apne business ko online grow karein — professional, mobile-friendly website with WhatsApp, Maps, gallery & SEO.");
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ business_name: "", owner_name: "", mobile: "", whatsapp: "", city: "", category: "", existing_website: "", requirements: "", budget: "", message: "" });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name || !f.mobile) { toast.error("Business name and mobile are required"); return; }
    setLoading(true);
    try { await api.post("/website-leads", f); setDone(true); window.scrollTo(0, 0); }
    catch (e) { toast.error("Could not submit. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <>
      <section className="relative bg-[#0f1115] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/20 text-primary px-4 py-1.5 text-sm font-semibold"><Zap className="w-4 h-4" /> OyeBest Web Services</span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl font-extrabold leading-tight">Apne Business Ko Online Grow Karein.</h1>
            <p className="mt-4 text-white/70 text-lg">Professional business website — mobile-friendly, WhatsApp-ready, Google Maps integrated aur SEO-friendly. Sab kuch, ek jagah.</p>
            <Button onClick={() => document.getElementById("lead-form").scrollIntoView({ behavior: "smooth" })} className="mt-6 rounded-full h-12 px-8 font-semibold gap-2" data-testid="gw-hero-cta"><Globe className="w-4 h-4" /> Get Your Website</Button>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl hidden md:block">
            <img src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1000&q=85" alt="Website design" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-center">Sab kuch aapke business ke liye</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {FEATURES.map((x) => (
            <div key={x.t} className="rounded-2xl border border-border p-5 flex items-start gap-3 bg-white">
              <span className="w-11 h-11 rounded-xl bg-accent text-primary flex items-center justify-center shrink-0"><x.icon className="w-5 h-5" /></span>
              <div><p className="font-bold">{x.t}</p><p className="text-sm text-muted-foreground">{x.s}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section id="lead-form" className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        {done ? (
          <div className="text-center py-16 rounded-3xl bg-accent border border-primary/20">
            <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-8 h-8" /></div>
            <h2 className="font-display text-2xl font-bold">Dhanyavaad! Request mil gayi.</h2>
            <p className="text-muted-foreground mt-2">Our team will contact you shortly to discuss your website.</p>
            <Button variant="outline" className="mt-6 rounded-full" onClick={() => navigate("/")}>Back to Home</Button>
          </div>
        ) : (
          <div className="rounded-3xl border border-border p-6 sm:p-8 bg-white shadow-sm">
            <h2 className="font-display text-2xl font-bold">Get Your Website</h2>
            <p className="text-muted-foreground mt-1">Fill this form and we'll reach out to you.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Business Name *"><Input value={f.business_name} onChange={(e) => set("business_name", e.target.value)} className="rounded-xl h-12" data-testid="gw-business" /></Field>
                <Field label="Owner Name"><Input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="rounded-xl h-12" data-testid="gw-owner" /></Field>
                <Field label="Mobile *"><Input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} className="rounded-xl h-12" data-testid="gw-mobile" /></Field>
                <Field label="WhatsApp"><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="rounded-xl h-12" data-testid="gw-whatsapp" /></Field>
                <Field label="City">
                  <Select value={f.city} onValueChange={(v) => set("city", v)}>
                    <SelectTrigger className="rounded-xl h-12" data-testid="gw-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Category">
                  <Select value={f.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger className="rounded-xl h-12" data-testid="gw-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Existing Website"><Input value={f.existing_website} onChange={(e) => set("existing_website", e.target.value)} className="rounded-xl h-12" placeholder="If any" data-testid="gw-existing" /></Field>
                <Field label="Budget (optional)"><Input value={f.budget} onChange={(e) => set("budget", e.target.value)} className="rounded-xl h-12" data-testid="gw-budget" /></Field>
              </div>
              <Field label="Requirements"><Textarea value={f.requirements} onChange={(e) => set("requirements", e.target.value)} className="rounded-xl" rows={2} data-testid="gw-requirements" /></Field>
              <Field label="Message"><Textarea value={f.message} onChange={(e) => set("message", e.target.value)} className="rounded-xl" rows={2} data-testid="gw-message" /></Field>
              <Button type="submit" disabled={loading} className="w-full rounded-full h-12 font-semibold" data-testid="gw-submit">{loading ? "Submitting…" : "Get Your Website"}</Button>
            </form>
          </div>
        )}
      </section>
    </>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm font-semibold">{label}</Label>{children}</div>;
}
